import { useQuery } from '@tanstack/react-query';
import type { TenantBranding } from '@cusp/contracts';
import { identityScope, type Identity } from '../identity.js';
import { queryKeys } from '../queryKeys.js';
import { request } from '../request.js';
import { useIdentity } from '../useIdentity.js';

/**
 * The acting institution's brand: one colour, one mark, one default direction.
 *
 * Read by whatever paints — `useBrandSync` in `@cusp/theme` for the document's
 * accent tokens, the rail's institution card and the learner masthead for the
 * logo. Every field is nullable and null is the ORDINARY state: an unbranded
 * school wears Cusp's own colours, which is what every tenant did before this
 * existed and what individual purchasers will do forever.
 *
 * A LONG `staleTime`, unlike its neighbours. A brand changes when an
 * administrator saves the settings screen and at no other time, so refetching
 * it on every mount would spend a request per screen on an answer that has not
 * moved in months — and this read is mounted by the app's root, so "every
 * screen" is literal. The editor invalidates this key explicitly on save, which
 * is what makes a long window safe rather than merely cheap: freshness comes
 * from the write, not from the poll.
 */
const TEN_MINUTES = 10 * 60 * 1000;

// A membership picker can read each row before selecting an acting tenant.
// Capture the SAME membership in both the key and the request headers; reading
// a mark must never switch the identity store or paint the document.
export function useTenantBranding(membership?: Identity | null) {
  const acting = useIdentity();
  const identity = membership === undefined ? acting : membership;
  return useQuery({
    queryKey: queryKeys.tenantBranding(identityScope(identity)),
    queryFn: ({ signal }) =>
      request<TenantBranding>('/v1/me/branding', { signal, identity: identity! }),
    // resolveContext 400s without the header triple — don't bother asking.
    enabled: identity !== null,
    staleTime: TEN_MINUTES,
  });
}
